import { useOne } from "@refinedev/core";
import { BrandKit, BrandKitAsset } from "pages/media/types";
import { useMemo } from "react";
import {
  ASSET_REFRESH_INTERVAL,
  ASSET_STALE_TIME,
} from "pages/media/constants";
import { wait } from "utils/";

/** Retries refetchBrandKit until `purpose` shows up (BE attaches new assets async), instead of a flat wait. */
export async function pollForBrandKitAsset(
  refetchBrandKit: () => Promise<{ data?: { data: BrandKit } }>,
  purpose: BrandKitAsset["purpose"],
  { attempts = 5, delayMs = 800 }: { attempts?: number; delayMs?: number } = {}
): Promise<BrandKitAsset | undefined> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    const result = await refetchBrandKit();
    const asset = result.data?.data.assets.find((a) => a.purpose === purpose);
    if (asset) return asset;
    if (attempt < attempts - 1) await wait(delayMs);
  }
  return undefined;
}

export const useBrandKit = ({
  brandKitId,
  organizationId,
}: {
  brandKitId?: string | null;
  organizationId?: string;
}) => {
  const {
    data: brandKit,
    isLoading: isBrandKitLoading,
    refetch: refetchBrandKit,
  } = useOne<BrandKit>({
    resource: `media/${organizationId}/brand_kits`,
    id: brandKitId ?? undefined,
    queryOptions: {
      enabled: !!brandKitId && !!organizationId,
      staleTime: ASSET_STALE_TIME,
      refetchInterval: ASSET_REFRESH_INTERVAL,
    },
  });

  const isBrandKitDataLoading =
    brandKitId && organizationId ? isBrandKitLoading : false;

  const brandKitPalette = brandKit?.data.palette;
  const brandKitAssets = brandKit?.data.assets;

  const assetsByPurpose = useMemo(() => {
    if (!brandKitAssets) return {};

    return {
      logo: brandKitAssets.find((asset) => asset.purpose === "Logo"),
      titleFont: brandKitAssets.find((asset) => asset.purpose === "Title"),
      textFont: brandKitAssets.find((asset) => asset.purpose === "Text"),
      subtitlesFont: brandKitAssets.find(
        (asset) => asset.purpose === "Subtitles"
      ),
      audio: brandKitAssets.find((asset) => asset.purpose === "Music"),
      backgroundPicture: brandKitAssets.find(
        (asset) => asset.purpose === "Background" && asset.type === "Picture"
      ),
      backgroundVideo: brandKitAssets.find(
        (asset) => asset.purpose === "Background" && asset.type === "Video"
      ),
      intro: brandKitAssets.find((asset) => asset.purpose === "Introduction"),
      transition: brandKitAssets.find(
        (asset) => asset.purpose === "Transition"
      ),
      banner: brandKitAssets.find((asset) => asset.purpose === "Banner"),
      closing: brandKitAssets.find((asset) => asset.purpose === "Closing"),
    };
  }, [brandKitAssets]);

  const {
    logo,
    titleFont,
    textFont,
    subtitlesFont,
    audio,
    backgroundPicture,
    backgroundVideo,
    intro,
    transition,
    banner,
    closing,
  } = assetsByPurpose;

  return {
    audio,
    backgroundBrandKitPicture: backgroundPicture,
    backgroundBrandKitVideo: backgroundVideo,
    banner,
    brandKit,
    brandKitName: brandKit?.data.name,
    brandKitPalette,
    closing,
    intro,
    isBrandKitLoading: isBrandKitDataLoading,
    logo,
    subtitlesFont,
    textFont,
    titleFont,
    transition,
    refetchBrandKit,
  };
};
